/**
 * GameCalendarView Component
 * 
 * Outlook-style timeline calendar view showing tasks as blocks.
 * Displays hours on the left with task blocks positioned by time.
 */

import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SquiggleMascot } from '../SquiggleMascot';
import { GameSpeechBubble } from '../GameSpeechBubble';
import { hexToRgba, readableTextColor } from '../../../utils/colorUtils';
import styles from './GameCalendarView.module.scss';

// Generic types that work with any task/routine shape
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTask = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRoutine = any;

export interface GameCalendarViewProps {
  routines: AnyRoutine[];
  tasks: AnyTask[];
  selectedDate: Date;
  hasAnyTasks: boolean;
  isLoading: boolean;
  onTaskClick?: (taskId: string) => void;
}

// Helper functions to normalize task fields
const getTaskName = (task: AnyTask): string => 
  task.label || task.name || 'Untitled Task';
const getStartTime = (task: AnyTask): string => 
  task.start_time || task.start || '00:00';
const getEndTime = (task: AnyTask): string => 
  task.end_time || task.end || '00:00';

// Convert time string to minutes from midnight
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Hour labels for the calendar (6 AM to 10 PM)
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 6 AM to 10 PM

// Zoom range for the timeline, in pixels per hour. Zooming in reveals more
// per-task detail; zooming out compresses the day so the times are still legible.
const DEFAULT_HOUR_HEIGHT = 88;
const MIN_HOUR_HEIGHT = 44;
const MAX_HOUR_HEIGHT = 260;

const DEFAULT_TASK_COLOR = '#5C6DB0';

// Horizontal layout of overlapping blocks.
const RIGHT_GUTTER = 16; // matches the timeline's right padding ($spacing-md)
const COL_GAP = 6; // space between side-by-side columns

// Assign each task a column so time-overlapping tasks render side-by-side
// instead of stacking on top of (and occluding) each other. Tasks are grouped
// into clusters of transitively-overlapping blocks; within a cluster, each task
// is greedily placed in the first column that's free at its start time. Returns
// a map of task id -> { colIndex, colCount } where colCount is the cluster width.
const assignColumns = (
  tasks: { id: string; startMinutes: number; endMinutes: number }[],
): Map<string, { colIndex: number; colCount: number }> => {
  const result = new Map<string, { colIndex: number; colCount: number }>();
  let cluster: typeof tasks = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length === 0) return;
    const colEnds: number[] = []; // end-minute of the last task placed in each column
    const colOf = new Map<string, number>();
    for (const t of cluster) {
      let col = colEnds.findIndex((end) => end <= t.startMinutes);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(t.endMinutes);
      } else {
        colEnds[col] = t.endMinutes;
      }
      colOf.set(t.id, col);
    }
    const colCount = colEnds.length;
    for (const t of cluster) {
      result.set(t.id, { colIndex: colOf.get(t.id) ?? 0, colCount });
    }
    cluster = [];
  };

  for (const t of tasks) {
    if (cluster.length > 0 && t.startMinutes >= clusterEnd) {
      flush();
      clusterEnd = -Infinity;
    }
    cluster.push(t);
    clusterEnd = Math.max(clusterEnd, t.endMinutes);
  }
  flush();
  return result;
};

// Inline left/width for a block given its column position within its cluster.
const columnStyle = (colIndex: number, colCount: number): React.CSSProperties => {
  if (colCount <= 1) {
    return { left: 0, width: `calc(100% - ${RIGHT_GUTTER}px)`, right: 'auto' };
  }
  const totalGaps = (colCount - 1) * COL_GAP;
  const colW = `((100% - ${RIGHT_GUTTER}px - ${totalGaps}px) / ${colCount})`;
  return {
    left: `calc(${colW} * ${colIndex} + ${colIndex * COL_GAP}px)`,
    width: `calc(${colW})`,
    right: 'auto',
  };
};

export const GameCalendarView: React.FC<GameCalendarViewProps> = ({
  routines,
  hasAnyTasks,
  isLoading,
  onTaskClick,
}) => {
  // Pixels per hour — drives vertical scale. Adjustable via pinch or +/- buttons.
  const [hourHeight, setHourHeight] = useState(DEFAULT_HOUR_HEIGHT);
  const pinchRef = useRef<{ startDist: number; startHeight: number } | null>(null);

  // Flatten all tasks with normalized fields
  const allTasks = useMemo(() => {
    return routines.flatMap((routine: AnyRoutine) =>
      (routine.tasks || []).map((task: AnyTask) => ({
        id: task.id,
        name: getTaskName(task),
        startTime: getStartTime(task),
        endTime: getEndTime(task),
        startMinutes: timeToMinutes(getStartTime(task)),
        endMinutes: timeToMinutes(getEndTime(task)),
        routineName: routine.name,
        routineId: routine.id,
        color: task.color || routine.color || DEFAULT_TASK_COLOR,
      }))
    ).sort((a, b) => a.startMinutes - b.startMinutes);
  }, [routines]);

  const clampHeight = (h: number) => Math.max(MIN_HOUR_HEIGHT, Math.min(MAX_HOUR_HEIGHT, h));

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { startDist: Math.hypot(dx, dy), startHeight: hourHeight };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const scale = dist / pinchRef.current.startDist;
      setHourHeight(clampHeight(pinchRef.current.startHeight * scale));
    }
  };

  const handleTouchEnd = () => {
    pinchRef.current = null;
  };

  // Find the time range to display
  const timeRange = useMemo(() => {
    if (allTasks.length === 0) {
      return { startHour: 6, endHour: 22 };
    }
    const minStart = Math.min(...allTasks.map(t => t.startMinutes));
    const maxEnd = Math.max(...allTasks.map(t => t.endMinutes));
    return {
      startHour: Math.max(0, Math.floor(minStart / 60) - 1),
      endHour: Math.min(24, Math.ceil(maxEnd / 60) + 1),
    };
  }, [allTasks]);

  const formatHour = (hour: number): string => {
    if (hour === 0 || hour === 24) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
  };

  // Compact time without AM/PM — the hour rail on the left carries the AM/PM context.
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <SquiggleMascot mood="idle" size="md" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!hasAnyTasks) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyMessage}>Your day is clear!</p>
        <div className={styles.squiggleArea}>
          <SquiggleMascot mood="encouraging" size="md" />
          <GameSpeechBubble tailDirection="left">
            <p className={styles.bubbleText}>
              Switch to list view to add routines
            </p>
          </GameSpeechBubble>
        </div>
      </div>
    );
  }

  // Calculate visible hours
  const visibleHours = HOURS.filter(
    h => h >= timeRange.startHour && h <= timeRange.endHour
  );

  const MIN_TAP_HEIGHT = 26; // Keep even the shortest task tappable and able to show one text line.
  const startMinutesOfDay = timeRange.startHour * 60;

  // Group overlapping tasks into side-by-side columns so they don't occlude
  // each other.
  const columns = assignColumns(allTasks);

  // Position every block at its true time so it lines up with the hour rail on
  // the left. Height is proportional to duration (zoom changes the scale).
  const placedTasks = allTasks.map((task) => {
    const top = ((task.startMinutes - startMinutesOfDay) / 60) * hourHeight;
    const duration = Math.max(task.endMinutes - task.startMinutes, 0);
    const height = Math.max((duration / 60) * hourHeight, MIN_TAP_HEIGHT);
    const { colIndex, colCount } = columns.get(task.id) ?? { colIndex: 0, colCount: 1 };
    return { task, top, height, colIndex, colCount };
  });

  const containerHeight = visibleHours.length * hourHeight;

  return (
    <div className={styles.calendarView}>
      {/* Zoom controls */}
      <div className={styles.zoomControls}>
        <button
          type="button"
          className={styles.zoomButton}
          onClick={() => setHourHeight((h) => clampHeight(h - 32))}
          disabled={hourHeight <= MIN_HOUR_HEIGHT}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          className={styles.zoomButton}
          onClick={() => setHourHeight((h) => clampHeight(h + 32))}
          disabled={hourHeight >= MAX_HOUR_HEIGHT}
          aria-label="Zoom in"
        >
          +
        </button>
      </div>

      <div
        className={styles.timeline}
        style={{ height: containerHeight }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Time grid */}
        <div className={styles.timeGrid}>
          {visibleHours.map((hour) => (
            <div key={hour} className={styles.hourRow} style={{ height: hourHeight }}>
              <span className={styles.hourLabel}>{formatHour(hour)}</span>
              <div className={styles.hourLine} />
            </div>
          ))}
        </div>

        {/* Task blocks */}
        <div className={styles.tasksContainer}>
          {placedTasks.map(({ task, top, height, colIndex, colCount }, index) => {
            // The name sits inline next to the time so even short blocks show it;
            // the routine name only appears once the block is tall enough.
            const showRoutine = height >= 52;
            const textColor = readableTextColor(task.color);
            return (
              <motion.button
                key={task.id}
                className={styles.taskBlock}
                style={{
                  top,
                  height,
                  ...columnStyle(colIndex, colCount),
                  background: hexToRgba(task.color, 0.9),
                  borderLeftColor: task.color,
                  color: textColor,
                }}
                onClick={() => onTaskClick?.(task.id)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                type="button"
              >
                <span className={styles.taskHeader}>
                  <span className={styles.taskTime} style={{ color: textColor }}>
                    {formatTime(task.startTime)} – {formatTime(task.endTime)}
                  </span>
                  <span className={styles.taskName} style={{ color: textColor }}>
                    {task.name}
                  </span>
                </span>
                {showRoutine && (
                  <span className={styles.taskRoutine} style={{ color: textColor, opacity: 0.7 }}>
                    {task.routineName}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GameCalendarView;
